import { AppButton, FormSection } from "../../../../../components/ui";
import {
  Box,
  Modal,
  Stack,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import ScheduleSelector from "react-schedule-selector";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  AdminLeadStatus,
  ApplicationRole,
  AttorneyApprovalStatus,
  AttorneyLeadStatus,
  Category,
  IntentResponse,
  LeadsService,
  LeadViewModel,
  OpenAPI,
  PaymentsService,
  ReferralCaseProgressViewModel,
  UserShortResponse,
} from "../../../../../api";
import {
  setPaymentAmount,
  setPaymentIntent,
  setPaymentSecret,
} from "../../../../../redux/actions/paymentActions";
import {
  startSpinner,
  stopSpinner,
} from "../../../../../redux/actions/spinnerActions";
import useNavigateSearch from "../../../../../services/useNavigateSearch";
import {
  errorInstructions,
  LeadType,
} from "../../shared/Constants";
import LeadSideRailPortal from "./LeadSideRailPortal";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CaseStatusSummary from "../CaseStatusSummary";

const MySwal = withReactContent(Swal);

interface ReferralProcessActionsProps {
  leadType: LeadType;
  userRoles?: ApplicationRole[];
  details?: LeadViewModel;
}

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

export default function ReferralProcessActions(
  props: ReferralProcessActionsProps
) {
  const [isSendAgreement, setIsSendAgreement] = useState<boolean>(false);
  const [initiateIntake] = useState<boolean>(false);

  const [schedule, setSchedule] = useState<Date[]>([]);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [intakeStatus] = useState<number>(0);

  const [winningAttorney, setWinningAttorney] = useState<UserShortResponse>();
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [caseProgress, setCaseProgress] = useState<ReferralCaseProgressViewModel | null>();

  const navigate = useNavigateSearch();
  const dispatch = useDispatch();
  useEffect(() => {
    window.addEventListener("message", handleDocumentEvent);
    return function cleanup() {
      window.removeEventListener("message", handleDocumentEvent);
    };
  }, []);

  const showAdminFee = props.details?.category !== Category.WORKERS_COMPENSATION;


  const handleDocumentEvent = (e) => {
    const type = e.data && e.data.type;
    const payload = e.data && e.data.payload;

    switch (type) {
      case "session_view.document.loaded":
        console.log("Session view is loaded");
        break;
      case "session_view.document.completed":
        LeadsService.postApiLeadsAgreementSign(props.details!.id!)
          .then(() => {
            MySwal.fire({
              icon: "success",
              title: <p className="title">Agreement successfully signed.</p>,
            });
            window.location.reload();
          })
          .catch(() =>
            MySwal.fire({
              icon: "error",
              title: <p className="title">Error signing agreement.</p>,
              html: <div className="alert-text">{errorInstructions}</div>,
            })
          );

        setSignUrl(null);
        console.log("Document is completed");
        console.log(payload);
        break;
      case "session_view.document.exception":
        console.log("Exception during document finalization");
        console.log(payload);
    }
  };

  useEffect(() => {
    if (
      props.details?.status === AdminLeadStatus.WINNER_SELECTED &&
      (props.userRoles?.includes(ApplicationRole.ADMIN) || props.userRoles?.includes(ApplicationRole.REFFERAL_STAFF))
    ) {
      LeadsService.getApiLeadsWinner(props.details?.id!).then((winner) =>
        setWinningAttorney(winner)
      );
    }
  }, [props.details]);

  const postWinStatuses = [
    AttorneyLeadStatus.WON,
    AttorneyLeadStatus.SIGNED,
    AttorneyLeadStatus.PAID,
  ];

  useEffect(() => {
    if (!props.details?.id) return;
    const isPostWin = postWinStatuses.includes(props.details.attorneyLeadStatus!);
    const isAdmin =
      props.userRoles?.includes(ApplicationRole.ADMIN) ||
      props.userRoles?.includes(ApplicationRole.REFFERAL_STAFF);
    if (isPostWin || isAdmin) {
      LeadsService.getApiLeadsReferralCaseProgress(props.details.id)
        .then((data) => setCaseProgress(data ?? null))
        .catch(() => setCaseProgress(null));
    }
  }, [props.details]);

  const canUpdateCaseProgress =
    props.details?.attorneyLeadStatus === AttorneyLeadStatus.PAID ||
    (props.details?.attorneyLeadStatus === AttorneyLeadStatus.SIGNED &&
      props.details?.withoutPayment === true);

  const showCaseStatusPanel =
    (props.userRoles?.includes(ApplicationRole.ADMIN) ||
      props.userRoles?.includes(ApplicationRole.REFFERAL_STAFF)) &&
    postWinStatuses.includes(props.details?.attorneyLeadStatus!);

  const handleEditCaseStatus = () =>
    navigate(`/lead/${props.details?.id}/referral-case`);

  const handleShareClick = () => {
    navigate(`/lead/send/${props.details?.id}`);
  };

  const handleIntakeReject = () => {
    MySwal.fire({
      title: <strong>Please provide a reason for failure</strong>,
      input: "text",
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "Back",
      confirmButtonText: "Reject intake",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    })
      .then((result) => {
        // TODO: sent on success
        if (result.isConfirmed)
          MySwal.fire({
            icon: "success",
            title: <p className="title">Intake rejected!</p>,
            html: (
              <div className="alert-text">
                You can select a new winner for this lead now.
              </div>
            ),
          }).then(() => {
            window.location.reload();
          });
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error rejecting lead.</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      });
  };
  const handleConfirmIntake = () => {
    MySwal.fire({
      title: <strong>Confirm intake</strong>,
      html: (
        <div>
          You are about to confirm {winningAttorney?.firstName}{" "}
          {winningAttorney?.lastName} intake of this lead. Are you sure you want
          to continue?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, send now",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    })
      .then((result) => {
        // TODO: sent on success
        if (result.isConfirmed) {
          MySwal.fire({
            icon: "success",
            title: <p className="title">Intake confirmed</p>,
            html: (
              <div className="alert-text">
                You can now send agreement to {winningAttorney?.firstName}{" "}
                {winningAttorney?.lastName}.
              </div>
            ),
          }).then(() => {
            window.location.reload();
          });
          handleIntakeStatusChange(true);
        }
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error confirming intake.</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      });
  };
  const handleIntakeStatusChange = (value) => {
    //TODO: on reject send lead back to shared status, on approve - allow agreement send
    setIsSendAgreement(value);
  };

  const handleLeadAccept = () => {
    if (!!initiateIntake || intakeStatus === 1) {
      setOpenModal(true);
      return;
    }

    MySwal.fire({
      title: <strong>Confirm accept</strong>,
      html: (
        <>
          <p className="title">
            By accepting this lead, you’ll be taken to the agreement page to finalize the process by signing.
            Please confirm your action.
          </p>
        </>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, accept",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(startSpinner());
        LeadsService.postApiLeadsAcceptLead(props.details?.id!, {})/* {single ? "Lead Accepted. Please proceed with next steps." : "Please wait for GLio confirmation. You will be notified of the final decision."} */
          .then((single) => {
            if (single) {
                window.location.reload();
            } else {
              MySwal.fire({
                icon: "info",
                title: (
                  <p className="title">
                    Please wait for GLio confirmation. You will be notified of the final decision.
                  </p>
                ),
              }).then(() => {
                navigate(`/attorney-leads`);
              });
            }
          })
          .finally(() => dispatch(stopSpinner()));
      }
    }).catch(() => {
      MySwal.fire({
        icon: "error",
        title: <p className="title">Error accepting lead</p>,
        html: (
          <div className="alert-text">
            {errorInstructions}
          </div>
        ),
      });
    });
  };

  const handleAcceptConfirm = () => {
    MySwal.fire({
      icon: "success",
      title: <p className="title">Lead accepted</p>,
    }).then(() => {
      setOpenModal(false);
      schedule.forEach((sch) => {
        var dat = new Date(sch);
        console.log(dat.toLocaleString());
      });
      //TODO: redirect to table
    });
  };

  const handleLeadReject = () => {
    MySwal.fire({
      title: <strong>Confirm rejection</strong>,
      html: (
        <div>
          Please provide a reason for rejection. You cannot undo this action.
          Are you sure you want to continue?
        </div>
      ),
      input: "text",
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, reject",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
      allowOutsideClick: false,
      allowEnterKey: false,
      allowEscapeKey: false,
    })
      .then((result) => {
        if (!result.isConfirmed) return;
        
        LeadsService.postApiLeadsRejectLead(props.details?.id!, {
          reason: result.value,
        }).then(() => {
          MySwal.fire({
            icon: "success",
            title: <p className="title">Lead rejected</p>,
          }).then(() => {
            navigate(`/attorney-leads`);
          });
        });
      })
      .catch(() => {
        MySwal.fire({
          icon: "error",
          title: <p className="title">Error rejecting lead.</p>,
          html: <div className="alert-text">{errorInstructions}</div>,
        });
      });
  };

  const handlePay = () => {
    if (!props.details?.totalFee) {
      MySwal.fire({
        icon: "error",
        title: <p className="title">Error inititating payment.</p>,
        html: <div className="alert-text">{errorInstructions}</div>,
      });
      return;
    }
    PaymentsService.postApiPaymentsSecret(
      props.details!.id!
    ).then((response: IntentResponse) => {
      dispatch(setPaymentAmount({ adminFee: props.details?.adminFee, totalFee: props.details?.totalFee, stripeFee: props.details?.stripeFee }));
      dispatch(setPaymentSecret(response.secret));
      dispatch(setPaymentIntent(response.intent));
      navigate(`/checkout/${props.details?.id}`);
    });
  };

  const handleSign = () => {
    LeadsService.postApiLeadsAgreementSignurl(props.details!.id!).then(
      (url) => {
        setSignUrl(url);
      }
    );
  };

  const handleSendAgreement = () => {
    MySwal.fire({
      title: <strong>Confirm approval</strong>,
      html: (
        <div>
          You are about to send agreement to {winningAttorney?.firstName}{" "}
          {winningAttorney?.lastName}. Are you sure you want to continue?
        </div>
      ),
      icon: "question",
      showCancelButton: true,
      cancelButtonText: "No, go back",
      confirmButtonText: "Yes, send now",
      confirmButtonColor: "#ed633a",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        LeadsService.postApiLeadsAgreementSend(props.details!.id!)
          .then((res) => {
            console.log(res);
            MySwal.fire({
              icon: "success",
              title: <p className="title">Success!</p>,
              html: (
                <div className="alert-text">
                  Agreement was sent, you will be notified when it is signed.
                </div>
              ),
            }).then(() => {
              navigate("/referral-leads");
            });
          })

          .catch(() => {
            MySwal.fire({
              icon: "error",
              title: <p className="title">Error sending agreement.</p>,
              html: <div className="alert-text">{errorInstructions}</div>,
            });
          });
      }
    });
  };

  const downloadClick = () => {
    if (!!props.details?.id) {
      fetch(`${OpenAPI.BASE}/api/Leads/${props.details.id}/pdfIntake`, {
        method: "GET",
        headers: {
          Authorization: (OpenAPI.HEADERS as any)?.Authorization,
        },
      }).then((response: any) => {
        response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);

          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          if (props.details?.status != AdminLeadStatus.PAID) {
            a.download = `Intake notes.pdf`;
          }
          else {
            a.download = `Intake notes for ${props.details?.firstName} ${props.details?.lastName}.pdf`;
          }
          document.body.appendChild(a);
          a.click();
        });
      });
    }
  };

  return (
    <>
      <LeadSideRailPortal>
        <>
          {props.leadType === LeadType.Admin &&
            props.details?.status === AdminLeadStatus.NEW &&
            props.details.attorneyApprovalStatus ===
              AttorneyApprovalStatus.REFER && (
              <FormSection
                className="lead-side-panel"
                title="Share lead"
                subtitle="Share this referral with attorneys."
              >
                <AppButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleShareClick}
                >
                  Share
                </AppButton>
              </FormSection>
            )}

          {props.leadType === LeadType.Admin &&
            props.details?.status === AdminLeadStatus.INTRODUCTION &&
            !isSendAgreement && (
              <FormSection
                className="lead-side-panel"
                title="Intake result"
                subtitle="Confirm whether the intake succeeded."
              >
                <Stack spacing={1.5}>
                  <AppButton
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleConfirmIntake}
                  >
                    Intake successful
                  </AppButton>
                  <AppButton
                    variant="outlined"
                    color="inherit"
                    fullWidth
                    onClick={() => handleIntakeReject()}
                  >
                    Intake failed
                  </AppButton>
                </Stack>
              </FormSection>
            )}

          {props.details?.status !== AdminLeadStatus.DRAFT &&
            !props.details?.isArchived &&
            (props.leadType === LeadType.Attorney ||
              props.leadType === LeadType.Case) && (
              <>
                {props.details?.attorneyLeadStatus ===
                  AttorneyLeadStatus.NEW && (
                  <FormSection
                    className="lead-side-panel"
                    title="Respond to lead"
                    subtitle={
                      showAdminFee
                        ? `Admin fee: $${props.details!.adminFee!.toLocaleString("en-US")}`
                        : "Accept or reject this shared lead."
                    }
                  >
                    <Stack spacing={1.5}>
                      <AppButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleLeadAccept}
                      >
                        Accept
                      </AppButton>
                      <AppButton
                        variant="outlined"
                        color="inherit"
                        fullWidth
                        onClick={handleLeadReject}
                      >
                        Reject
                      </AppButton>
                    </Stack>
                  </FormSection>
                )}

                {props.details?.attorneyLeadStatus ===
                  AttorneyLeadStatus.ACCEPTED && (
                  <FormSection
                    className="lead-side-panel"
                    title="Lead progress"
                    subtitle="Awaiting GL.io confirmation"
                  />
                )}

                {props.details?.attorneyLeadStatus ===
                  AttorneyLeadStatus.WON &&
                  props.details.status === AdminLeadStatus.SENT_FOR_SIGNING &&
                  !signUrl && (
                    <FormSection
                      className="lead-side-panel"
                      title="Agreement"
                      subtitle="Sign the referral agreement."
                    >
                      <AppButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handleSign}
                      >
                        Sign
                      </AppButton>
                    </FormSection>
                  )}

                {props.details?.attorneyLeadStatus ===
                  AttorneyLeadStatus.SIGNED &&
                  props.details?.withoutPayment !== true && (
                    <FormSection
                      className="lead-side-panel"
                      title="Payment"
                      subtitle={
                        showAdminFee
                          ? `Admin fee: $${props.details!.adminFee!.toLocaleString("en-US")}`
                          : "Pay the admin fee to continue."
                      }
                    >
                      <AppButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={handlePay}
                      >
                        Pay
                      </AppButton>
                    </FormSection>
                  )}

                {(props.details?.attorneyLeadStatus ===
                  AttorneyLeadStatus.PAID ||
                  props.details?.withoutPayment === true) && (
                  <FormSection
                    className="lead-side-panel"
                    title="Case actions"
                    subtitle="Download intake notes or update case progress."
                  >
                    <Stack spacing={1.5}>
                      <AppButton
                        variant="contained"
                        color="primary"
                        fullWidth
                        onClick={downloadClick}
                      >
                        Download Pdf
                      </AppButton>
                      {canUpdateCaseProgress && (
                        <AppButton
                          variant="outlined"
                          color="primary"
                          fullWidth
                          onClick={handleEditCaseStatus}
                        >
                          Update case status
                        </AppButton>
                      )}
                    </Stack>
                  </FormSection>
                )}
              </>
            )}

        {props.details?.status === AdminLeadStatus.WINNER_SELECTED &&
          (props.userRoles?.includes(ApplicationRole.ADMIN) || props.userRoles?.includes(ApplicationRole.REFFERAL_STAFF)) && (
            <FormSection
              className="lead-side-panel"
              title="Winning attorney"
              subtitle={
                winningAttorney
                  ? `${winningAttorney.firstName ?? ""} ${winningAttorney.lastName ?? ""}`.trim()
                  : "Loading…"
              }
              icon={<GavelOutlinedIcon />}
            >
              <AppButton
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleSendAgreement}
              >
                Send agreement
              </AppButton>
            </FormSection>
          )}
        {props.leadType === LeadType.Admin && (
          <>
            {props.details?.status === AdminLeadStatus.INTRODUCTION && (
              <FormSection
                className="lead-side-panel"
                title="Intake attorney"
                subtitle={
                  winningAttorney
                    ? `${winningAttorney.firstName ?? ""} ${winningAttorney.lastName ?? ""}`.trim()
                    : undefined
                }
                icon={<InfoOutlinedIcon />}
              >
                <div className="lead-side-panel__meta">
                  <div>Phone: 12345</div>
                  <div>Email: brendan@gmail.com</div>
                  <div className="mt-2">Available times:</div>
                  <div>4/28 12PM</div>
                  <div>4/28 2PM</div>
                  <div>4/28 3PM</div>
                  <div>4/29 11AM</div>
                  <div>4/29 12PM</div>
                  <div>4/30 12PM</div>
                </div>
              </FormSection>
            )}

            {props.details?.status === AdminLeadStatus.SENT_FOR_SIGNING && (
              <FormSection
                className="lead-side-panel"
                title="Winning attorney"
                subtitle={
                  winningAttorney
                    ? `${winningAttorney.firstName ?? ""} ${winningAttorney.lastName ?? ""}`.trim()
                    : undefined
                }
                icon={<GavelOutlinedIcon />}
              >
                <div className="lead-side-panel__meta">
                  Agreement status: Waiting for signature
                </div>
              </FormSection>
            )}

            {props.details?.status === AdminLeadStatus.SIGNED && props.details?.withoutPayment !== true && (
              <FormSection
                className="lead-side-panel"
                title="Winning attorney"
                subtitle={
                  winningAttorney
                    ? `${winningAttorney.firstName ?? ""} ${winningAttorney.lastName ?? ""}`.trim()
                    : undefined
                }
                icon={<GavelOutlinedIcon />}
              >
                <div className="lead-side-panel__meta">
                  Agreement status: Signed, awaiting payment
                </div>
              </FormSection>
            )}

          </>
        )}
        {showCaseStatusPanel && (
          <CaseStatusSummary
            className="lead-side-panel"
            progress={caseProgress}
            leadId={props.details?.id}
            canEdit={canUpdateCaseProgress}
            onEdit={handleEditCaseStatus}
          />
        )}
        </>
      </LeadSideRailPortal>

      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <h5 id="modal-title" className="title text-center">
            {
              // TODO: update text when required by admin
            }
            You requested intake for this lead, please select at least 3 times
            that you are available and we will notify you once intake is
            confirmed.
          </h5>
          <div id="modal-description">
            <ScheduleSelector
              selection={schedule}
              numDays={5}
              minTime={8}
              maxTime={19}
              hourlyChunks={1}
              onChange={setSchedule}
            ></ScheduleSelector>
          </div>
          <div className="row mt-5 justify-content-between">
            <AppButton
              variant="contained"
              color="secondary"
              className="col-6 col-md-2"
              onClick={() => setOpenModal(false)}
            >
              Back
            </AppButton>
            <AppButton
              variant="contained"
              color="primary"
              className="col-6 col-md-2"
              onClick={handleAcceptConfirm}
            >
              Confirm
            </AppButton>
          </div>
        </Box>
      </Modal>

      {!!signUrl && (
        <div className="row">
          <iframe
            title="signing"
            src={`https://app.pandadoc.com/s/${signUrl}`}
            width="800"
            height="800"
          ></iframe>
        </div>
      )}
    </>
  );
}
